/**
 * SOLID Review
 * S — Strong. One concern: the balance decisions, each built from the snapshot as `Credits`, asked, and answered
 *     through a `ClientDecision`. Whose balance it is, is the aggregate's rule.
 * O — Good. A new source of a balance is a method here, a row in the table and its move on `Credits`.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. One role, `IServiceDecisionTable`: the Facade sees the payment ops' table and nothing else.
 * D — Strong. The credits aggregate, the decision builder and the schemas.
 */
import { Credits } from '../../payment/Credits';
import { ClientDecision, type TClientDecision } from '../ClientDecision';
import type { IServiceDecisionTable, TDecisionRows } from '../IServiceDecisionTable';
import type { IPropsClientInput } from '../../ports/schemas/ClientChanges';
import type { IEntityTableAck } from '../../ports/schemas/TableEvents';
import type { ICommandCreditsChanged, ICommandBalanceRead, IPayloadAccountWatch } from '../../ports/schemas/PaymentMoves';

/** The ops this service answers for on the client Domain's lollipop. */
type TPaymentOp = 'creditsChanged' | 'balanceRead' | 'planAccountWatch';

/**
 * THE ACCOUNT'S CREDITS (frontend-domain-oo P6, 2026-10-26; handbook §28.273) — the decide functions of
 * `decisions/payment/credits.ts` (FE4c: AppRoot's push handler, PaymentSuccessScreen's poll) as a service.
 */
export class Payment implements IServiceDecisionTable<TPaymentOp> {
  /** Its decisions, by op: what `ClientDecisions` merges and answers through. */
  readonly decisions: TDecisionRows<TPaymentOp> = {
    creditsChanged: (input, command) => this.creditsChanged(input, command),
    balanceRead: (input, command) => this.balanceRead(input, command),
    planAccountWatch: (input) => this.planAccountWatch(input),
  };

  /** A balance the server pushed for a player. */
  creditsChanged(input: IPropsClientInput, command: ICommandCreditsChanged): TClientDecision<IEntityTableAck> {
    const credits = Credits.of(input.session);
    return ClientDecision.keeping(input, credits).acked(credits.changed(command));
  }

  /** The account's own balance, read back after a purchase. */
  balanceRead(input: IPropsClientInput, command: ICommandBalanceRead): TClientDecision<IEntityTableAck> {
    const credits = Credits.of(input.session);
    return ClientDecision.keeping(input, credits).acked(credits.balanceRead(command.credits));
  }

  /** Whose pushed balance to hear. */
  planAccountWatch(input: IPropsClientInput): TClientDecision<IPayloadAccountWatch> {
    return ClientDecision.unchanged().answering(Credits.of(input.session).watched());
  }
}
